<template>
    <section class="text-setting__section">
        <div class="text-setting__content">
            <header class="text-setting__header">
                <h2 class="text-setting__title">{{ label }}</h2>
            </header>

            <ul class="text-setting__list">
                <li class="text-setting__item">
                    <control-font-family :value="setting.fontFamily" @input="onInput" />
                </li>
            </ul>
        </div>

        <figure class="text-setting__preview">
            <div class="text-setting__preview-container" :class="previewClass">
                <dynamic-element
                    :style="previewStyles"
                    :tag="tag"
                    contenteditable
                    @input="onPreviewTextInput"
                    v-text="previewTextNew"
                />
            </div>
        </figure>
    </section>
</template>

<script>
import { mapState } from 'vuex';

import ControlFontFamily from '@/components/settings/shared/controls/control-font-family';
import DynamicElement from '@/components/utilities/dynamic-element';
import mapFilters from '@/utils/map-filters';
import { STATE_CLASSES } from '@/data/constants/classes';

export default {
    name: 'settings-headings',

    components: {
        DynamicElement,
        ControlFontFamily,
    },

    props: {
        label: {
            type: String,
            required: true,
        },

        tag: {
            type: String,
            default: 'h1',
        },

        previewText: {
            type: String,
            required: true,
        },

        type: {
            type: String,
            default: 'heading',
        },
    },

    data() {
        return {
            previewTextNew: this.previewText,
            updatingPreviewText: false,
            setting: {},
        };
    },

    computed: {
        ...mapState({
            settings: state => state.settings,
        }),

        previewClass() {
            switch (this.type) {
                case 'heading':
                    return STATE_CLASSES.HEADING;

                case 'paragraph':
                    return STATE_CLASSES.PARAGRAPH;

                case 'button':
                    return STATE_CLASSES.BUTTON;

                default:
                    return '';
            }
        },

        previewStyles() {
            const previewStyle = { ...this.setting };

            delete previewStyle.css;

            return previewStyle;
        },
    },

    watch: {
        previewText(previewText) {
            // we don't want to update the component the user is typing into
            // otherwise this forces a re-render which moves the cursor to the beginning of the input text after each keystroke
            if (this.updatingPreviewText) {
                this.updatingPreviewText = false;
                return;
            }

            this.previewTextNew = previewText;
        },

        settings: {
            handler(settingsNew) {
                if (!settingsNew) return;

                this.setting = settingsNew.text[this.tag];
            },
            immediate: true,
        },
    },

    methods: {
        ...mapFilters(['paramCase']),

        onInput({ tailwindDefinitionProp, value }) {
            this.setting[tailwindDefinitionProp] = value;
            this.generateCssString();

            this.$emit('input', {
                ...this.settings.text,
                [this.tag]: this.setting,
            });
        },

        onPreviewTextInput(e) {
            const { innerText } = e.target;

            this.updatingPreviewText = true;

            // notify other preview boxes of the change, so they can be kept in sync
            this.$emit('preview-input', this.tag, innerText);
        },

        generateCssString() {
            let cssString = `${ this.tag } { `;

            Object.keys(this.setting).forEach((prop) => {
                if (prop === 'css') return;

                cssString += `${ this.paramCase(prop) }: ${ this.setting[prop] }; `;
            });

            cssString += '}';

            this.setting.css = cssString;
        },
    },
};
</script>
